import {
    Directive,
    ElementRef,
    Output,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    inject,
} from '@angular/core';

/**
 * Emits when a click happens outside of the host element.
 *
 * Usage:
 * - Place on any element that should react to outside clicks.
 * - Optionally provide elements to exclude from outside check.
 *
 * Inputs:
 * - `excludeElement?: ElementRef` — A single element to ignore.
 * - `excludeElements?: ElementRef[]` — A list of elements to ignore.
 *
 * Outputs:
 * - `clickOutside` — Emits when user clicks outside of host and excluded elements.
 *
 * Example:
 * ```html
 * <div clickOutside (clickOutside)="close()"
 *      [excludeElement]="button">
 *   Panel content
 * </div>
 * <button #button type="button" (click)="toggle()">Toggle</button>
 * ```
 */
@Directive({
    selector: '[clickOutside]',
    standalone: true,
})
export class ClickOutsideDirective implements OnDestroy {
    protected elementRef = inject(ElementRef);

    /**
     * Single element to ignore when checking for outside clicks.
     */
    @Input() excludeElement: ElementRef;

    /**
     * Multiple elements to ignore when checking for outside clicks.
     */
    @Input() excludeElements: ElementRef[];

    @Output()
    /**
     * Emits when a click is made outside of host and excluded elements.
     */
    public clickOutside = new EventEmitter();

    @HostListener('document:click', ['$event.target'])
    /**
     * Global click listener that compares the target with host and excluded elements.
     */
    public onClick(targetElement) {
        const clickedInside = this.elementRef.nativeElement.contains(targetElement);
        if (this.excludeElement && this.excludeElement?.nativeElement?.contains(targetElement)) {
            return;
        }
        if (
            this.excludeElements &&
            this.excludeElements.find((el) => el?.nativeElement === targetElement)
        ) {
            return;
        }
        if (!clickedInside) {
            this.clickOutside.emit(null);
        }
    }

    /**
     * Cleanup references to help GC.
     */
    ngOnDestroy() {
        this.excludeElement = null;
        this.excludeElements = null;
    }
}
